import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertOctagon, AlertTriangle, Info, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useUXChrome } from "../contexts/UXChromeContext";
import { api } from "../lib/api";

const READ_KEY = "asl_notif_read";
const SEV_TONE = {
    critical: "bg-rose-50 text-rose-700 border-rose-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
};
const SEV_ICON = {
    critical: AlertOctagon,
    warn: AlertTriangle,
    info: Info,
};

function getRead() {
    try { return JSON.parse(localStorage.getItem(READ_KEY) || "[]"); } catch { return []; }
}
function setRead(ids) {
    localStorage.setItem(READ_KEY, JSON.stringify(ids));
}

export default function NotificationBell() {
    const { notificationsOpen, setNotificationsOpen } = useUXChrome();
    const [items, setItems] = useState([]);
    const [readIds, setReadIds] = useState(getRead());
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data.items || []);
        } catch { /* silent */ }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => { if (notificationsOpen) load(); }, [notificationsOpen]);

    const unread = items.filter((it) => !readIds.includes(it.id));
    const unreadCount = unread.length;

    const open = (item) => {
        const next = [...new Set([...readIds, item.id])];
        setReadIds(next);
        setRead(next);
        setNotificationsOpen(false);
        if (item.link) navigate(item.link);
    };

    const markAllRead = () => {
        const next = [...new Set([...readIds, ...items.map((i) => i.id)])];
        setReadIds(next);
        setRead(next);
    };

    return (
        <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
            <PopoverTrigger asChild>
                <button
                    className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-gray-100 transition asl-navy-text"
                    aria-label="Notifications"
                    data-testid="notif-bell"
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full asl-crimson-bg text-white flex items-center justify-center mono"
                            data-testid="notif-badge"
                        >
                            {unreadCount}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0" data-testid="notif-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--asl-border)" }}>
                    <div className="text-sm font-bold asl-navy-text">Notifications</div>
                    {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-[11px] asl-navy-text hover:underline" data-testid="notif-mark-all">
                            Mark all read
                        </button>
                    )}
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="px-4 py-10 text-center text-sm text-[color:var(--asl-muted)]" data-testid="notif-empty">
                            <Check className="h-6 w-6 mx-auto mb-2 text-emerald-600" /> All clear ✓
                        </div>
                    ) : (
                        items.map((it) => {
                            const Icon = SEV_ICON[it.severity] || Info;
                            const isRead = readIds.includes(it.id);
                            return (
                                <button
                                    key={it.id}
                                    onClick={() => open(it)}
                                    data-testid={`notif-item-${it.id}`}
                                    className={`w-full text-left px-4 py-3 text-sm flex items-start gap-3 hover:bg-gray-50 ${isRead ? "opacity-70" : ""}`}
                                    style={{ borderBottom: "1px solid var(--asl-border)" }}
                                >
                                    <span className={`mt-0.5 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border ${SEV_TONE[it.severity] || SEV_TONE.info}`}>
                                        {it.severity}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="asl-navy-text">{it.message}</div>
                                    </div>
                                    <Icon className="h-4 w-4 text-[color:var(--asl-muted)] shrink-0" />
                                </button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
